import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { ProtectedRoute, AdminRoute, ProFeatureRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Assessment from "@/pages/Assessment";
import Result from "@/pages/Result";
import Wrapped from "@/pages/Wrapped";
import Roadmap from "@/pages/Roadmap";
import Pro from "@/pages/Pro";
import Welcome from "@/pages/Welcome";
import SharedDna from "@/pages/SharedDna";
import AuthCallback from "@/pages/AuthCallback";
import Legal from "@/pages/Legal";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import Home from "@/pages/app/Home";
import Career from "@/pages/app/Career";
import Missions from "@/pages/app/Missions";
import Coach from "@/pages/app/Coach";
import Profile from "@/pages/app/Profile";
import Simulator from "@/pages/app/Simulator";
import ResumePage from "@/pages/app/Resume";
import Interview from "@/pages/app/Interview";
import Report from "@/pages/app/Report";
import Compare from "@/pages/app/Compare";
import Proof from "@/pages/app/Proof";
import Notebook from "@/pages/app/Notebook";
import Negotiation from "@/pages/app/Negotiation";
import Upskill from "@/pages/app/Upskill";
import { ComingSoon } from "@/components/ComingSoon";
import Admin from "@/pages/admin/Admin";

function AppRoutes() {
  const location = useLocation();
  // Emergent Google auth returns to /auth/callback#session_id=...; handle it before any protected route runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/privacy" element={<Legal kind="privacy" />} />
          <Route path="/terms" element={<Legal kind="terms" />} />
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/signup" element={<Auth mode="signup" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dna/:code" element={<SharedDna />} />

          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/result" element={<Result />} />
          <Route path="/wrapped" element={<ProtectedRoute><Wrapped /></ProtectedRoute>} />
          <Route path="/roadmap" element={<ProtectedRoute><Roadmap /></ProtectedRoute>} />
          <Route path="/pro" element={<Pro />} />
          <Route path="/paywall" element={<Navigate to="/pro" replace />} />
          <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

          <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route index element={<Navigate to="/app/home" replace />} />
            <Route path="home" element={<Home />} />
            <Route path="career" element={<Career />} />
            <Route path="missions" element={<Missions />} />
            <Route path="coach" element={<Coach />} />
            <Route path="profile" element={<Profile />} />
            <Route path="simulator" element={<ComingSoon title="Career Simulator" blurb="Run what-if scenarios like switching to product, and see the 2-year outcome."><Simulator /></ComingSoon>} />
            <Route path="upskill" element={<Upskill />} />
            <Route path="notebook" element={<Notebook />} />
            <Route path="resume" element={<ProFeatureRoute feature="cv"><ResumePage /></ProFeatureRoute>} />
            <Route path="negotiation" element={<ProFeatureRoute feature="negotiation"><Negotiation /></ProFeatureRoute>} />
            <Route path="interview" element={<ProFeatureRoute feature="interview"><Interview /></ProFeatureRoute>} />
            <Route path="report" element={<Report />} />
            <Route path="compare" element={<Compare />} />
            <Route path="proof" element={<Navigate to="/pro?feature=portfolio" replace />} />
          </Route>

          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AppRoutes />
      </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
